import BasicButton from "../../common/components/BasicButton";
import LabeledInput from "../../common/components/LabeledInput";
import Modal, { ModalProps } from "../../common/components/Modal";
import SelectBox from "../../common/components/SelectBox";
import { useForm } from "../../../hooks/useForm";
import {
  useCreateAdminUserMutation,
  useGetAdminRolesQuery,
  useGetAdminUserQuery,
  useUpdateAdminUserMutation,
} from "../../../lib/apollo/graphql/generated";
import Loader from "../../common/components/Loader";
import Input from "../../common/components/Input";
import PasswordErrorMessage from "./PasswordErrorMessage";

const ADMIN_ROLES_KR = {
  ADMIN: "일반 관리자",
  SUB_ADMIN: "서브 관리자",
};

interface UpdateAdminUserModalProps extends ModalProps {
  adminId: string | null;
}

export default function UpdateAdminUserModal({
  adminId,
  open,
  onClose,
}: UpdateAdminUserModalProps) {
  const [form, onChange, onBatch] = useForm({
    email: "",
    name: "",
    roleId: "",
    status: "PENDING",
    password: "",
    confirmPassword: "",
  });

  const isCreate = !adminId;

  const { loading } = useGetAdminUserQuery({
    variables: {
      adminId: adminId!,
    },
    skip: !adminId,
    onCompleted: (res) => {
      const admin = res.getAdminUser;

      onBatch({
        email: admin.email,
        name: admin.name,
        roleId: admin.adminRole.id,
        status: admin.status,
      });
    },
  });

  const { data, loading: rolesLoading } = useGetAdminRolesQuery({
    onCompleted: (res) => {
      const role = res?.getAdminRoles;
      onChange("roleId", role[0].id);
    },
  });

  const [updateAdminUser, { loading: updateLoading }] =
    useUpdateAdminUserMutation({
      onCompleted: () => {
        window.alert("운영자 정보를 수정했습니다");
        onClose();
      },
      refetchQueries: ["GetAdminUsers"],
      onError: (error) => {
        const isEmailDuplicated =
          error.message.includes("이미 존재하는 이메일입니다");

        if (isEmailDuplicated) {
        }
      },
    });

  const [createAdminUser, { loading: createLoading }] =
    useCreateAdminUserMutation({
      onCompleted: () => {
        window.alert("새로운 운영자를 등록했습니다");
        onClose();
      },
      refetchQueries: ["GetAdminUsers"],
      onError: (error) => {
        const isEmailDuplicated =
          error.message.includes("이미 존재하는 이메일입니다");

        if (isEmailDuplicated) {
        }
      },
    });

  const isPasswordEmpty = isCreate && !form.password && !form.confirmPassword;
  const isPasswordMismatch = form.password !== form.confirmPassword;

  const isSubmitDisabled =
    createLoading ||
    updateLoading ||
    loading ||
    rolesLoading ||
    !form.email ||
    !form.name ||
    !form.roleId ||
    !form.status ||
    isPasswordEmpty ||
    isPasswordMismatch;

  const onSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    if (isCreate) {
      createAdminUser({
        variables: {
          createAdminUserDto: {
            email: form.email.trim(),
            name: form.name,
            status: form.status,
            adminRoleId: form.roleId,
            password: form.password,
          },
        },
      });
    } else {
      updateAdminUser({
        variables: {
          updateAdminUserDto: {
            adminId: adminId,
            email: form.email.trim(),
            name: form.name,
            status: form.status,
            adminRoleId: form.roleId,
            ...(form.password && { password: form.password }),
          },
        },
      });
    }
  };

  if (loading || rolesLoading) {
    return (
      <Modal
        title={`운영자 ${isCreate ? "등록" : "수정"}`}
        open={open}
        onClose={onClose}
        height="700px"
      >
        <Loader />
      </Modal>
    );
  }

  const roles = data?.getAdminRoles || [];

  return (
    <Modal
      title={`운영자 ${isCreate ? "등록" : "수정"}`}
      open={open}
      onClose={onClose}
      height="830px"
    >
      <form onSubmit={onSubmit} className="flex h-full w-full flex-col gap-5">
        <LabeledInput
          label="ID"
          value={form.email}
          onChange={(event) => onChange("email", event.target.value.trim())}
        />
        <LabeledInput
          label="이름"
          value={form.name}
          onChange={(event) => onChange("name", event.target.value)}
        />
        <div className="flex flex-col gap-1">
          <LabeledInput
            placeholder="비밀번호"
            label="비밀번호"
            value={form.password}
            type="password"
            onChange={(event) =>
              onChange("password", event.target.value.trim())
            }
          />
          <Input
            placeholder="비밀번호 확인"
            value={form.confirmPassword}
            type="password"
            onValueChange={(value) => onChange("confirmPassword", value)}
            className="h-[40px] w-full"
          />
        </div>
        <SelectBox
          label="권한"
          value={form.roleId}
          onSelect={(value) => onChange("roleId", value)}
          options={roles?.map((role) => ({
            label: ADMIN_ROLES_KR[role.name as keyof typeof ADMIN_ROLES_KR],
            value: role.id,
          }))}
          className="h-[50px]"
        />
        <SelectBox
          label="상태"
          value={form.status}
          onSelect={(value) => onChange("status", value)}
          options={[
            { label: "활성", value: "ACTIVE" },
            { label: "비활성", value: "INACTIVE" },
            { label: "대기", value: "PENDING" },
          ]}
          className="h-[50px]"
        />
        <div className="mt-auto flex w-full flex-col items-center gap-1 self-center">
          <PasswordErrorMessage
            isCreate={isCreate}
            password={form.password}
            confirmPassword={form.confirmPassword}
          />
          <BasicButton
            type="submit"
            loading={updateLoading || createLoading}
            disabled={isSubmitDisabled}
            text={isCreate ? "등록" : "수정"}
            className="mt-auto w-1/2 self-center"
          />
        </div>
      </form>
    </Modal>
  );
}
